import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';
import { useAuth } from '../../contexts/AuthContext';
import AppImage from '../AppImage';
import NotificationCenter from '../NotificationCenter';


const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { signOut, isAuthenticated, currentTenant, isGymOwner, isTrainer, isStaff, isSuperAdmin, tenantMembership, unreadCount } = useAuth();
  const [notificationCount] = useState(3);
  const [showToolsDropdown, setShowToolsDropdown] = useState(false);
  const [showReportsDropdown, setShowReportsDropdown] = useState(false);

  // Request notification permission on mount
  React.useEffect(() => {
    if (isAuthenticated) {
      import('../../services/notificationService')?.then(module => {
        module.default?.requestPermission();
      });
    }
  }, [isAuthenticated]);

  const navigationItems = [
    {
      label: 'Dashboard',
      path: (isGymOwner || isSuperAdmin) ? '/admin-dashboard' : '/member-dashboard',
      icon: 'LayoutDashboard',
      description: 'Your personalized fitness hub'
    },
    {
      label: 'User Management',
      path: '/user-management-dashboard',
      icon: 'Users',
      description: 'Create and manage gym staff and members',
      showForRoles: ['gym_owner']
    },
    {
      label: 'Member Registration',
      path: '/manual-member-registration-portal',
      icon: 'UserPlus',
      description: 'Manually register new members',
      showForRoles: ['gym_owner', 'staff']
    },
    {
      label: 'Access Control',
      path: '/real-time-member-access-control',
      icon: 'Shield',
      description: 'Real-time facility access monitoring',
      showForRoles: ['gym_owner', 'trainer', 'staff']
    },
    {
      label: 'Attendance',
      path: '/barcode-attendance-system',
      icon: 'Scan',
      description: 'Barcode scanning and attendance tracking',
      showForRoles: ['gym_owner', 'trainer', 'staff']
    },
    {
      label: 'QR Code Management',
      path: '/qr-code-management-center',
      icon: 'QrCode',
      description: 'Generate and manage member QR codes',
      showForRoles: ['gym_owner', 'trainer', 'staff']
    },
    {
      label: 'QR Scanner',
      path: '/qr-code-scanner-validator',
      icon: 'ScanLine',
      description: 'Scan and validate member QR codes',
      showForRoles: ['gym_owner', 'trainer', 'staff']
    },
    {
      label: 'My Barcode',
      path: '/member-barcode-generator',
      icon: 'QrCode',
      description: 'View and manage your membership barcode'
    },
    {
      label: 'Workouts',
      path: '/workout-library',
      icon: 'Dumbbell',
      description: 'Exercise library and active sessions'
    },
    {
      label: 'Referrals',
      path: '/member-referral-system',
      icon: 'Users',
      description: 'Invite friends and earn rewards'
    },
    {
      label: 'Recovery',
      path: '/recovery-insights',
      icon: 'Activity',
      description: 'Track sleep, HRV, and rest days'
    },
    {
      label: 'Progress',
      path: '/progress-analytics',
      icon: 'TrendingUp',
      description: 'Track your fitness journey'
    },
    {
      label: 'Community',
      path: '/community-feed',
      icon: 'Users',
      description: 'Social feed and member posts'
    },
    {
      label: 'Classes',
      path: '/class-schedule',
      icon: 'Calendar',
      description: 'Book fitness classes'
    },
    {
      label: 'Chat',
      path: '/member-chat-system',
      icon: 'MessageCircle',
      description: 'Message gym members and trainers'
    },
    {
      label: 'Challenges',
      path: '/challenge-system',
      icon: 'Trophy',
      description: 'Compete in fitness challenges'
    },
    {
      label: 'Leaderboards',
      path: '/social-leaderboards',
      icon: 'Award',
      description: 'Compete with gym members'
    },
    {
      label: 'Memberships',
      path: '/membership-management-dashboard',
      icon: 'CreditCard',
      description: 'Manage membership plans and subscriptions',
      showForRoles: ['gym_owner']
    },
    {
      label: 'Billing Center',
      path: '/billing-subscription-center',
      icon: 'Receipt',
      description: 'Billing operations and payment processing',
      showForRoles: ['gym_owner']
    },
    {
      label: 'My Billing',
      path: '/member-billing-portal',
      icon: 'Wallet',
      description: 'View your subscription and payment history'
    },
    {
      label: 'Tenant Management',
      path: '/super-admin-tenant-management',
      icon: 'Building2',
      description: 'Manage all gym locations and billing',
      showForRoles: ['super_admin']
    },
    {
      label: 'Announcements',
      path: '/gym-announcements',
      icon: 'Megaphone',
      description: 'Send gym announcements',
      showForRoles: ['gym_owner', 'trainer', 'staff']
    },
    {
      label: 'Settings',
      path: '/gym-settings',
      icon: 'Settings',
      description: 'Gym branding and settings',
      showForRoles: ['gym_owner']
    },
    {
      label: 'Profile',
      path: '/member-profile',
      icon: 'User',
      description: 'Account settings and preferences'
    },
    {
      label: 'Payment Processors',
      path: '/payment-processor-integration',
      icon: 'CreditCard',
      description: 'Configure Stripe and PayPal integration',
      showForRoles: ['gym_owner']
    },
    {
      label: 'Payment Methods',
      path: '/member-payment-methods',
      icon: 'Wallet',
      description: 'Manage your payment methods'
    },
    {
      label: 'Membership Alerts',
      path: '/membership-alerts-management',
      icon: 'Bell',
      description: 'Payment and subscription alerts',
      showForRoles: ['gym_owner', 'staff']
    },
    {
      label: 'AI Chatbot',
      path: '/ai-fitness-assistant-chatbot',
      icon: 'MessageSquare',
      description: 'AI-powered fitness assistant'
    },
    {
      label: 'Product Catalog',
      path: '/product-catalog-management',
      icon: 'Package',
      description: 'Manage product inventory and catalog',
      showForRoles: ['gym_owner', 'staff']
    },
    {
      label: 'Inventory Management',
      path: '/inventory-management-dashboard',
      icon: 'Warehouse',
      description: 'Real-time inventory oversight and management',
      showForRoles: ['gym_owner', 'staff']
    },
    {
      label: 'Inventory Sync',
      path: '/real-time-inventory-synchronization-center',
      icon: 'RefreshCw',
      description: 'Real-time synchronization across all systems',
      showForRoles: ['gym_owner', 'staff']
    },
    {
      label: 'Bulk Import',
      path: '/bulk-product-import-system',
      icon: 'Upload',
      description: 'Bulk product import with Excel templates',
      showForRoles: ['gym_owner', 'staff']
    },
    {
      label: 'Invoices',
      path: '/automated-invoice-management-system',
      icon: 'FileText',
      description: 'Automated invoice generation and management',
      showForRoles: ['gym_owner', 'staff']
    },
    {
      label: 'Support Tickets',
      path: '/ai-support-ticket-system',
      icon: 'Headphones',
      description: 'AI-powered support ticket management'
    }
  ];

  // Tools dropdown items
  const toolsItems = [
    {
      label: 'Log Workout',
      path: '/workout-completion-logger',
      icon: 'ClipboardCheck',
      description: 'Track your workout progress'
    },
    {
      label: 'AI Workouts',
      path: '/ai-workout-recommendations',
      icon: 'Sparkles',
      description: 'AI-powered workout plans'
    },
    {
      label: 'AI Nutrition',
      path: '/ai-nutrition-plans',
      icon: 'Utensils',
      description: 'AI-powered meal planning'
    },
    {
      label: 'Point of Sale',
      path: '/point-of-sale-dashboard',
      icon: 'ShoppingCart',
      description: 'Process sales and manage transactions',
      showForRoles: ['gym_owner', 'staff', 'trainer']
    },
    {
      label: 'Payroll Management',
      path: '/payroll-management-system',
      icon: 'DollarSign',
      description: 'Employee wage processing and payroll administration',
      showForRoles: ['gym_owner', 'staff']
    },
    {
      label: 'Staff Attendance',
      path: '/staff-attendance-management-system',
      icon: 'UserCheck',
      description: 'QR code-based staff attendance tracking',
      showForRoles: ['gym_owner', 'staff', 'trainer']
    },
    {
      label: 'Universal QR Generator',
      path: '/universal-qr-code-generator-management',
      icon: 'QrCode',
      description: 'Generate QR codes for all purposes',
      showForRoles: ['gym_owner', 'staff']
    }
  ];

  // Reports dropdown items
  const reportsItems = [
    {
      label: 'Custom Report Builder',
      path: '/advanced-report-builder',
      icon: 'FileSpreadsheet',
      description: 'Create custom reports with advanced filtering',
      showForRoles: ['gym_owner', 'staff']
    },
    {
      label: 'Attendance Reports',
      path: '/attendance-report-dashboard',
      icon: 'BarChart3',
      description: 'Attendance analytics and reporting',
      showForRoles: ['gym_owner', 'trainer', 'staff']
    },
    {
      label: 'Sales Analytics',
      path: '/sales-analytics-reporting',
      icon: 'TrendingUp',
      description: 'Sales performance and analytics',
      showForRoles: ['gym_owner', 'staff']
    },
    {
      label: 'Revenue Dashboard',
      path: '/revenue-dashboard',
      icon: 'DollarSign',
      description: 'Financial analytics and insights',
      showForRoles: ['gym_owner']
    },
    {
      label: 'Member Retention',
      path: '/member-retention-analytics',
      icon: 'Users',
      description: 'Member retention and churn analysis',
      showForRoles: ['gym_owner']
    },
    {
      label: 'Payment Reconciliation',
      path: '/payment-reconciliation-dashboard',
      icon: 'FileText',
      description: 'Reconcile payments with billing records',
      showForRoles: ['gym_owner']
    },
    {
      label: 'Cash Reconciliation',
      path: '/cash-drawer-reconciliation-center',
      icon: 'Calculator',
      description: 'Cash drawer reconciliation and variance alerts',
      showForRoles: ['gym_owner', 'staff']
    },
    {
      label: 'Daily Cash Reports',
      path: '/daily-cash-reconciliation-reports',
      icon: 'Receipt',
      description: 'Daily cash reconciliation reports',
      showForRoles: ['gym_owner', 'staff']
    },
    {
      label: 'Workout Analytics',
      path: '/workout-analytics-dashboard',
      icon: 'Activity',
      description: 'Workout performance analytics',
      showForRoles: ['gym_owner', 'trainer']
    },
    {
      label: 'Feedback Analytics',
      path: '/feedback-analytics-dashboard',
      icon: 'MessageSquare',
      description: 'Member feedback and satisfaction analysis',
      showForRoles: ['gym_owner']
    }
  ];

  const isActivePath = (path) => {
    if (path === '/workout-library') {
      return location?.pathname === '/workout-library' || location?.pathname === '/active-workout';
    }
    if (path === '/progress-analytics') {
      return location?.pathname === '/progress-analytics' || location?.pathname === '/nutrition-tracker';
    }
    if (path === '/community-feed') {
      return location?.pathname === '/community-feed';
    }
    if (path === '/class-schedule') {
      return location?.pathname === '/class-schedule' || location?.pathname === '/trainer-profiles';
    }
    if (path === '/admin-dashboard' || path === '/member-dashboard') {
      return location?.pathname === '/admin-dashboard' || location?.pathname === '/member-dashboard';
    }
    if (path === '/gym-announcements') {
      return location?.pathname === '/gym-announcements';
    }
    if (path === '/social-leaderboards') {
      return location?.pathname === '/social-leaderboards';
    }
    if (path === '/member-chat-system') {
      return location?.pathname === '/member-chat-system';
    }
    if (path === '/challenge-system') {
      return location?.pathname === '/challenge-system';
    }
    return location?.pathname === path;
  };

  const hasNotification = (path) => {
    return path === '/class-schedule' && notificationCount > 0;
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  // Filter navigation items based on user role
  const getFilteredNavItems = () => {
    return navigationItems?.filter(item => {
      // If no role restriction, show to everyone
      if (!item?.showForRoles) return true;
      
      // Check if user's role matches any of the allowed roles
      if (isSuperAdmin && item?.showForRoles?.includes('super_admin')) return true;
      if (isGymOwner && item?.showForRoles?.includes('gym_owner')) return true;
      if (isStaff && item?.showForRoles?.includes('staff')) return true;
      if (isTrainer && item?.showForRoles?.includes('trainer')) return true;
      
      return false;
    });
  };

  const getFilteredToolsItems = () => {
    return toolsItems?.filter(item => {
      if (!item?.showForRoles) return true;
      if (isSuperAdmin && item?.showForRoles?.includes('super_admin')) return true;
      if (isGymOwner && item?.showForRoles?.includes('gym_owner')) return true;
      if (isStaff && item?.showForRoles?.includes('staff')) return true;
      if (isTrainer && item?.showForRoles?.includes('trainer')) return true;
      return false;
    });
  };

  const getFilteredReportsItems = () => {
    return reportsItems?.filter(item => {
      if (!item?.showForRoles) return true;
      if (isSuperAdmin && item?.showForRoles?.includes('super_admin')) return true;
      if (isGymOwner && item?.showForRoles?.includes('gym_owner')) return true;
      if (isStaff && item?.showForRoles?.includes('staff')) return true;
      if (isTrainer && item?.showForRoles?.includes('trainer')) return true;
      return false;
    });
  };

  const filteredNavItems = getFilteredNavItems();
  const filteredToolsItems = getFilteredToolsItems();
  const filteredReportsItems = getFilteredReportsItems();

  return (
    <header className="sticky top-0 z-[100] bg-card shadow-md transition-smooth">
      <div className="mx-auto max-w-[1440px] px-6">
        <div className="flex h-20 items-center justify-between">
          <Link 
            to={(isGymOwner || isSuperAdmin) ? '/admin-dashboard' : '/member-dashboard'}
            className="flex items-center gap-3 transition-smooth hover:opacity-80"
            aria-label="FitWellness Home"
          >
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10">
              <AppImage 
                src="/assets/images/More_Than_Technology_logo-01-1769218379125.jpg" 
                alt="FitWellness Logo" 
                className="h-10 w-10 object-contain"
              />
            </div>
            <div>
              <span className="text-2xl font-heading font-bold text-primary">
                FitWellness
              </span>
              {isSuperAdmin ? (
                <p className="text-xs text-primary font-semibold">Master Admin</p>
              ) : currentTenant ? (
                <p className="text-xs text-muted-foreground">{currentTenant?.name}</p>
              ) : null}
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-6">
            {filteredNavItems?.slice(0, 3)?.map((item) => (
              <a 
                key={item?.path}
                href={item?.path} 
                className="text-foreground hover:text-primary transition-colors"
              >
                {item?.label}
              </a>
            ))}

            {/* Tools Dropdown */}
            {filteredToolsItems?.length > 0 && (
              <div className="relative"
                onMouseEnter={() => setShowToolsDropdown(true)}
                onMouseLeave={() => setShowToolsDropdown(false)}
              >
                <button className="flex items-center gap-1 text-foreground hover:text-primary transition-colors">
                  Tools
                  <Icon name="ChevronDown" size={16} />
                </button>
                {showToolsDropdown && (
                  <div className="absolute top-full left-0 mt-2 w-72 bg-card border border-border rounded-lg shadow-lg py-2 z-50">
                    {filteredToolsItems?.map((item) => (
                      <Link
                        key={item?.path}
                        to={item?.path}
                        className="flex items-center gap-3 px-4 py-3 hover:bg-muted transition-smooth"
                        onClick={() => setShowToolsDropdown(false)}
                      >
                        <Icon name={item?.icon} size={18} className="text-primary flex-shrink-0" />
                        <div>
                          <p className="font-medium text-foreground">{item?.label}</p>
                          <p className="text-xs text-muted-foreground">{item?.description}</p>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Reports Dropdown */}
            {filteredReportsItems?.length > 0 && (
              <div className="relative"
                onMouseEnter={() => setShowReportsDropdown(true)}
                onMouseLeave={() => setShowReportsDropdown(false)}
              >
                <button className="flex items-center gap-1 text-foreground hover:text-primary transition-colors">
                  Reports
                  <Icon name="ChevronDown" size={16} />
                </button>
                {showReportsDropdown && (
                  <div className="absolute top-full left-0 mt-2 w-72 bg-card border border-border rounded-lg shadow-lg py-2 z-50">
                    {filteredReportsItems?.map((item) => (
                      <Link
                        key={item?.path}
                        to={item?.path}
                        className="flex items-center gap-3 px-4 py-3 hover:bg-muted transition-smooth"
                        onClick={() => setShowReportsDropdown(false)}
                      >
                        <Icon name={item?.icon} size={18} className="text-primary flex-shrink-0" />
                        <div>
                          <p className="font-medium text-foreground">{item?.label}</p>
                          <p className="text-xs text-muted-foreground">{item?.description}</p>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            )}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <NotificationCenter />
            <Button
              variant="ghost"
              size="icon"
              onClick={() => {}}
              aria-label="Settings"
            >
              <Icon name="Settings" size={20} />
            </Button>
            {isAuthenticated && (
              <Button
                variant="outline"
                onClick={handleSignOut}
                aria-label="Sign Out"
              >
                <Icon name="LogOut" size={18} className="mr-2" />
                Sign Out
              </Button>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;